import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import {
  tmsApi,
  type OrderDocument,
  type UpdateOrderDocumentRequest,
  type UploadOrderDocumentRequest,
} from '../client';

/**
 * Stable query-key factory for document caches. Documents hang off an
 * order, so the list cache is keyed by order id. The download URLs
 * inside each DTO are short-lived SAS tokens (10-minute expiry), so the
 * cache uses a tight `staleTime` to avoid handing stale links to
 * `<a href>`.
 */
export const orderDocumentKeys = {
  all: ['tms', 'orderDocument'] as const,
  byOrder: (orderId: string) => ['tms', 'orderDocument', 'order', orderId] as const,
  byId: (id: string) => ['tms', 'orderDocument', 'id', id] as const,
};

/**
 * Fetch every document for an order. Each DTO carries a fresh
 * `downloadUrl` (Azure SAS, ~10 min) so the FE can hand it directly to
 * an `<a href>` without a per-row round-trip. Short `staleTime` because
 * those URLs expire.
 */
export function useOrderDocuments(
  orderId: string | null,
  options?: Omit<UseQueryOptions<OrderDocument[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<OrderDocument[], Error>({
    queryKey: orderId ? orderDocumentKeys.byOrder(orderId) : orderDocumentKeys.all,
    queryFn: () => {
      if (!orderId) throw new Error('No order id');
      return tmsApi.getOrderDocuments(orderId);
    },
    enabled: !!orderId,
    // Shorter than the rest of the suite because the SAS URLs expire in
    // ~10 minutes — better to refetch than to render dead links.
    staleTime: 60 * 1000,
    ...options,
  });
}

/** Fetch one document by id, with a fresh SAS `downloadUrl`. */
export function useOrderDocument(
  id: string | null,
  options?: Omit<UseQueryOptions<OrderDocument, Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<OrderDocument, Error>({
    queryKey: id ? orderDocumentKeys.byId(id) : orderDocumentKeys.all,
    queryFn: () => {
      if (!id) throw new Error('No document id');
      return tmsApi.getOrderDocument(id);
    },
    enabled: !!id,
    staleTime: 60 * 1000,
    ...options,
  });
}

/**
 * POST a new document (multipart/form-data). The BE returns the new
 * document id as a bare string — the caller invalidates the per-order
 * list cache so the Documents panel picks up the new row with its
 * fresh SAS URL.
 *
 * <p>
 * The `tmsApi.uploadOrderDocument` helper handles the `FormData`
 * assembly internally, so callers pass a typed
 * {@link UploadOrderDocumentRequest} without touching the wire format.
 * </p>
 */
export function useUploadOrderDocument() {
  const qc = useQueryClient();
  return useMutation<string, Error, UploadOrderDocumentRequest>({
    mutationKey: [...orderDocumentKeys.all, 'upload'],
    mutationFn: (body) => tmsApi.uploadOrderDocument(body),
    onSuccess: (_id, vars) => {
      qc.invalidateQueries({ queryKey: orderDocumentKeys.byOrder(vars.orderId) });
    },
  });
}

/**
 * PUT document metadata (description / type / task re-link). Replacing
 * the file itself = delete + upload. Caller passes `orderId` alongside
 * the body so we can invalidate the per-order list cache — the wire
 * body only carries the document id.
 */
export function useUpdateOrderDocument() {
  const qc = useQueryClient();
  return useMutation<
    string,
    Error,
    { orderId: string; body: UpdateOrderDocumentRequest }
  >({
    mutationKey: [...orderDocumentKeys.all, 'update'],
    mutationFn: ({ body }) => tmsApi.updateOrderDocument(body),
    onSuccess: (_id, { orderId, body }) => {
      qc.invalidateQueries({ queryKey: orderDocumentKeys.byOrder(orderId) });
      qc.invalidateQueries({ queryKey: orderDocumentKeys.byId(body.documentId) });
    },
  });
}

/**
 * DELETE a document by id. The BE soft-deletes the row and hard-deletes
 * the blob in the same transaction. Caller passes `orderId` so we can
 * invalidate the per-order list cache.
 */
export function useDeleteOrderDocument() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { orderId: string; documentId: string }>({
    mutationKey: [...orderDocumentKeys.all, 'delete'],
    mutationFn: ({ documentId }) => tmsApi.deleteOrderDocument(documentId),
    onSuccess: (_res, { orderId, documentId }) => {
      qc.invalidateQueries({ queryKey: orderDocumentKeys.byOrder(orderId) });
      qc.removeQueries({ queryKey: orderDocumentKeys.byId(documentId) });
    },
  });
}
